/**
 * Room management for a practice site — backed by FHIR Location (physicalType=ro).
 */
import {
  Alert,
  Badge,
  Button,
  Collapse,
  Group,
  Modal,
  NumberInput,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  ThemeIcon,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useDisclosure } from '@mantine/hooks';
import {
  IconChevronDown,
  IconChevronRight,
  IconDeviceDesktop,
  IconPlus,
  IconWheelchair,
} from '@tabler/icons-react';
import { useCallback, useMemo, useState } from 'react';
import { roomSchema } from '@/schemas';
import {
  clampHoursToParent,
  customHoursFitParent,
  locationHasWeeklyHours,
  type LocationWeeklyHours,
} from '@unify/fhir-model/resources/location';
import { WeeklyAvailabilityEditor } from '@/features/calendar/WeeklyAvailabilityEditor';
import { getProviderWeeklyAvailabilityUiDisableReason } from '@/features/calendar/providerAvailabilityValidation';
import { uiToWeeklyHours, weeklyHoursToDayWindows, weeklyHoursToUi } from '@/lib/locationWeeklyHoursUi';
import {
  LOCATION_RESOURCE_TABLE_COL_WIDTHS,
  locationResourceTableStyle,
} from './locationResourceTableLayout';

export type RoomType = 'therapy-room' | 'phone-booth';

export interface Room {
  id: string;
  name: string;
  type: RoomType;
  capacity: number;
  adaAccessible: boolean;
  status: 'active' | 'inactive';
  hours?: LocationWeeklyHours;
}

const ROOM_TYPE_LABELS: Record<RoomType, string> = {
  'therapy-room': 'Room',
  'phone-booth': 'Phone Booth',
};

const ROOM_TYPE_COLORS: Record<RoomType, string> = {
  'therapy-room': 'blue',
  'phone-booth': 'gray',
};

const emptyRoomForm = (): Omit<Room, 'id'> => ({
  name: '',
  type: 'therapy-room',
  capacity: 2,
  adaAccessible: false,
  status: 'active',
});

function getErrorMessage(err: unknown): string {
  if (err && typeof err === 'object' && 'outcome' in err) {
    const issue = (err as {
      outcome?: { issue?: Array<{ diagnostics?: string; details?: { text?: string } }> };
    }).outcome?.issue?.[0];
    if (issue?.diagnostics) return issue.diagnostics;
    if (issue?.details?.text) return issue.details.text;
  }
  return err instanceof Error ? err.message : 'Failed to delete room';
}

export interface FixedEquipmentInfo {
  roomId: string;
  equipmentNames: string[];
}

export interface RoomManagementSectionProps {
  rooms: Room[];
  siteHours: LocationWeeklyHours;
  /** Fixed equipment by room — used to show an indicator on rooms with assigned devices. */
  fixedEquipmentByRoom?: FixedEquipmentInfo[];
  canEdit: boolean;
  onSaveRoom: (editingRoomId: string | null, data: Omit<Room, 'id'>) => Promise<void>;
  onDeleteRoom: (roomId: string) => Promise<void>;
  onToggleRoomStatus: (roomId: string, active: boolean) => Promise<void>;
}

export function RoomManagementSection({
  rooms,
  siteHours,
  fixedEquipmentByRoom,
  canEdit,
  onSaveRoom,
  onDeleteRoom,
  onToggleRoomStatus,
}: RoomManagementSectionProps): JSX.Element {
  const [opened, { toggle }] = useDisclosure(true);
  const [roomModalOpen, setRoomModalOpen] = useState(false);
  const [editingRoomId, setEditingRoomId] = useState<string | null>(null);
  const [form, setForm] = useState<Omit<Room, 'id'>>(emptyRoomForm());
  const [customHours, setCustomHours] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [saveError, setSaveError] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const fixedEquipMap = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const entry of fixedEquipmentByRoom ?? []) {
      m.set(entry.roomId, entry.equipmentNames);
    }
    return m;
  }, [fixedEquipmentByRoom]);

  const openAdd = useCallback(() => {
    setEditingRoomId(null);
    setFormErrors({});
    setSaveError(null);
    setForm(emptyRoomForm());
    setCustomHours(false);
    setRoomModalOpen(true);
  }, []);

  const openEdit = useCallback((room: Room) => {
    setEditingRoomId(room.id);
    setFormErrors({});
    setSaveError(null);
    setCustomHours(locationHasWeeklyHours(room.hours));
    setForm({
      name: room.name,
      type: room.type,
      capacity: room.type === 'phone-booth' ? 1 : room.capacity,
      adaAccessible: room.adaAccessible,
      status: room.status,
      hours: locationHasWeeklyHours(room.hours)
        ? clampHoursToParent(room.hours ?? {}, siteHours)
        : room.hours,
    });
    setRoomModalOpen(true);
  }, [siteHours]);

  const handleSaveRoom = useCallback(async () => {
    setSaveError(null);
    const input = {
      ...form,
      capacity: form.type === 'phone-booth' ? 1 : form.capacity,
    };
    const result = roomSchema.safeParse(input);
    if (!result.success) {
      const errors: Record<string, string> = {};
      for (const issue of result.error.issues) {
        const key = issue.path[0]?.toString() ?? '_root';
        errors[key] = issue.message;
      }
      setFormErrors(errors);
      return;
    }
    setFormErrors({});
    const { name, type, capacity, adaAccessible, status } = result.data;
    let hours: LocationWeeklyHours | undefined;
    if (customHours) {
      hours = clampHoursToParent(uiToWeeklyHours(weeklyHoursToUi(form.hours ?? siteHours)), siteHours);
      const hoursReason = getProviderWeeklyAvailabilityUiDisableReason(weeklyHoursToUi(hours));
      if (hoursReason) {
        setSaveError(hoursReason);
        return;
      }
      if (!customHoursFitParent(hours, siteHours)) {
        setSaveError('Custom hours must be within location hours');
        return;
      }
    }

    setSaving(true);
    try {
      await onSaveRoom(editingRoomId, { name, type, capacity, adaAccessible, status, hours });
      setRoomModalOpen(false);
      setEditingRoomId(null);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save room');
    } finally {
      setSaving(false);
    }
  }, [customHours, editingRoomId, form, onSaveRoom, siteHours]);

  const confirmDelete = useCallback(async () => {
    if (!confirmDeleteId) return;
    setSaving(true);
    try {
      await onDeleteRoom(confirmDeleteId);
      setConfirmDeleteId(null);
    } catch (err) {
      notifications.show({
        title: 'Delete blocked',
        message: getErrorMessage(err),
        color: 'red',
      });
    } finally {
      setSaving(false);
    }
  }, [confirmDeleteId, onDeleteRoom]);

  const groupedCounts = {
    therapy: rooms.filter((r) => r.status === 'active' && r.type === 'therapy-room').length,
    booth: rooms.filter((r) => r.status === 'active' && r.type === 'phone-booth').length,
  };

  return (
    <>
      <Stack gap="xs" mt="md" pt="md" style={{ borderTop: '1px solid var(--mantine-color-gray-3)' }}>
        <Group justify="space-between" wrap="nowrap" style={{ width: '100%' }}>
          <UnstyledButton type="button" onClick={toggle} style={{ flex: 1, minWidth: 0 }}>
            <Group gap="xs" wrap="nowrap">
              {opened ? <IconChevronDown size={18} /> : <IconChevronRight size={18} />}
              <Text size="sm" fw={600}>
                Rooms & booths
              </Text>
              <Group gap={6}>
                <Badge size="xs" variant="light" color="blue">
                  {groupedCounts.therapy} room
                </Badge>
                <Badge size="xs" variant="light" color="gray">
                  {groupedCounts.booth} phone booth
                </Badge>
              </Group>
            </Group>
          </UnstyledButton>
          {canEdit && (
            <Button
              size="xs"
              variant="light"
              leftSection={<IconPlus size={14} />}
              onClick={openAdd}
            >
              Add room
            </Button>
          )}
        </Group>

        <Collapse in={opened}>
          {rooms.length === 0 ? (
            <Text size="sm" c="gray.6" py="sm">
              No rooms yet. Add rooms or phone booths for scheduling.
            </Text>
          ) : (
            <Table highlightOnHover mt="xs" style={locationResourceTableStyle}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={LOCATION_RESOURCE_TABLE_COL_WIDTHS.name}>Name</Table.Th>
                  <Table.Th w={LOCATION_RESOURCE_TABLE_COL_WIDTHS.typePortability}>Type</Table.Th>
                  <Table.Th w={LOCATION_RESOURCE_TABLE_COL_WIDTHS.capacityAssignedRoom}>
                    Capacity
                  </Table.Th>
                  <Table.Th w={LOCATION_RESOURCE_TABLE_COL_WIDTHS.status}>Status</Table.Th>
                  {canEdit && <Table.Th w={LOCATION_RESOURCE_TABLE_COL_WIDTHS.actions} />}
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rooms.map((room) => (
                  <Table.Tr
                    key={room.id}
                    onClick={() => canEdit && openEdit(room)}
                    style={{
                      opacity: room.status === 'inactive' ? 0.55 : 1,
                      cursor: canEdit ? 'pointer' : 'default',
                    }}
                  >
                    <Table.Td>
                      <Group gap={6} wrap="nowrap">
                        <Text size="sm" fw={500}>
                          {room.name}
                        </Text>
                        {locationHasWeeklyHours(room.hours) && (
                          <Badge size="xs" variant="light" color="gold">
                            Custom hours
                          </Badge>
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Group gap={6} wrap="nowrap">
                        <Badge size="sm" color={ROOM_TYPE_COLORS[room.type]} variant="light">
                          {ROOM_TYPE_LABELS[room.type]}
                        </Badge>
                        {room.adaAccessible && (
                          <Tooltip label="ADA accessible" withArrow>
                            <ThemeIcon size={18} radius="sm" variant="light" color="teal">
                              <IconWheelchair size={11} />
                            </ThemeIcon>
                          </Tooltip>
                        )}
                        {fixedEquipMap.has(room.id) && (
                          <Tooltip
                            label={fixedEquipMap.get(room.id)!.join(', ')}
                            withArrow
                          >
                            <ThemeIcon size={18} radius="sm" variant="light" color="violet">
                              <IconDeviceDesktop size={11} />
                            </ThemeIcon>
                          </Tooltip>
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{room.capacity}</Text>
                    </Table.Td>
                    <Table.Td onClick={(e) => e.stopPropagation()}>
                      <Group gap="xs" wrap="nowrap">
                        <Switch
                          size="xs"
                          disabled={!canEdit}
                          checked={room.status === 'active'}
                          onChange={(e) =>
                            void onToggleRoomStatus(room.id, e.currentTarget.checked)
                          }
                          aria-label={`${room.status === 'active' ? 'Deactivate' : 'Activate'} ${room.name}`}
                          styles={{ track: { cursor: canEdit ? 'pointer' : 'default' } }}
                        />
                        {room.status === 'inactive' && (
                          <Badge size="xs" color="gray" variant="outline">
                            Inactive
                          </Badge>
                        )}
                      </Group>
                    </Table.Td>
                    {canEdit && (
                      <Table.Td>
                        <Group gap={4} justify="flex-end">
                          <Button
                            size="xs"
                            variant="light"
                            onClick={(e) => {
                              e.stopPropagation();
                              openEdit(room);
                            }}
                            aria-label={`Edit ${room.name}`}
                          >
                            Edit
                          </Button>
                        </Group>
                      </Table.Td>
                    )}
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Collapse>
      </Stack>

      <Modal
        opened={roomModalOpen}
        onClose={() => {
          setRoomModalOpen(false);
          setEditingRoomId(null);
        }}
        title={editingRoomId ? 'Edit room' : 'Add room'}
        size="lg"
      >
        <Stack gap="md">
          {saveError && (
            <Alert color="red" withCloseButton onClose={() => setSaveError(null)}>
              {saveError}
            </Alert>
          )}
          <TextInput
            label="Room name"
            placeholder="e.g., Room 102"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            error={formErrors.name}
            required
          />
          <Select
            label="Room type"
            data={[
              { value: 'therapy-room', label: 'Room' },
              { value: 'phone-booth', label: 'Phone Booth' },
            ]}
            value={form.type}
            onChange={(v) =>
              setForm((f) => ({
                ...f,
                type: (v as RoomType) || 'therapy-room',
                capacity: v === 'phone-booth' ? 1 : f.capacity < 2 ? 2 : f.capacity,
              }))
            }
          />
          <NumberInput
            label="Capacity"
            min={1}
            max={50}
            disabled={form.type === 'phone-booth'}
            value={form.type === 'phone-booth' ? 1 : form.capacity}
            onChange={(v) => setForm((f) => ({ ...f, capacity: typeof v === 'number' ? v : 1 }))}
            description={form.type === 'phone-booth' ? 'Phone booths are always capacity 1.' : undefined}
          />
          <Switch
            label="ADA accessible"
            checked={form.adaAccessible}
            onChange={(e) => setForm((f) => ({ ...f, adaAccessible: e.currentTarget.checked }))}
          />
          <Switch
            label="Custom hours"
            description={customHours ? undefined : 'Uses location hours'}
            checked={customHours}
            onChange={(e) => {
              const on = e.currentTarget.checked;
              setCustomHours(on);
              setForm((f) => ({
                ...f,
                hours: on
                  ? clampHoursToParent(
                    f.hours && locationHasWeeklyHours(f.hours) ? f.hours : siteHours,
                    siteHours,
                  )
                  : undefined,
              }));
            }}
          />
          {customHours && (
            <Stack gap="xs">
              <Text size="xs" c="gray.6">
                Must stay within location hours (Mountain Time).
              </Text>
              <WeeklyAvailabilityEditor
                value={weeklyHoursToUi(form.hours ?? siteHours)}
                onChange={(weekly) =>
                  setForm((f) => ({ ...f, hours: clampHoursToParent(uiToWeeklyHours(weekly), siteHours) }))
                }
                dayWindows={weeklyHoursToDayWindows(siteHours)}
              />
            </Stack>
          )}
          <Group justify={editingRoomId ? 'space-between' : 'flex-end'} mt="md">
            {editingRoomId && (
              <Button
                variant="subtle"
                color="red"
                onClick={() => {
                  setRoomModalOpen(false);
                  setConfirmDeleteId(editingRoomId);
                }}
              >
                Delete
              </Button>
            )}
            <Group gap="sm">
              <Button variant="outline" onClick={() => setRoomModalOpen(false)}>
                Cancel
              </Button>
              <Button loading={saving} onClick={() => void handleSaveRoom()}>
                {editingRoomId ? 'Save' : 'Add'}
              </Button>
            </Group>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={confirmDeleteId !== null}
        onClose={() => setConfirmDeleteId(null)}
        title="Delete room"
        size="sm"
        centered
      >
        <Stack gap="md">
          <Text size="sm">
            Are you sure you want to delete{' '}
            <Text span fw={600}>
              {rooms.find((r) => r.id === confirmDeleteId)?.name}
            </Text>
            ? This cannot be undone.
          </Text>
          <Group justify="flex-end">
            <Button variant="outline" onClick={() => setConfirmDeleteId(null)}>
              Cancel
            </Button>
            <Button color="red" loading={saving} onClick={() => void confirmDelete()}>
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
